import apiPaths from "app/utils/apiPaths";

/**
 * Auth endpoints must never trigger the access-token refresh retry loop.
 * refreshToken / login 401s are terminal for the session.
 * logout 401s are expected when the access token is already dead.
 */
export function isAuthTerminalRequest(url = "") {
  if (!url || typeof url !== "string") return false;
  if (url.includes(apiPaths.REFREST_TOKEN) || url.includes("refreshToken")) {
    return true;
  }
  if (url.includes("/oauth/token")) return true;
  if (url.includes(apiPaths.LOGOUT) || /(?:^|\/)logout(?:\?|$)/.test(url)) {
    return true;
  }
  return false;
}

export function shouldAttemptTokenRefresh(error, originalRequest) {
  if (!error?.response || error.response.status !== 401) return false;
  if (!originalRequest || originalRequest._retry) return false;
  if (originalRequest.skipAuthRefresh) return false;
  if (isAuthTerminalRequest(originalRequest.url || "")) return false;
  return true;
}

/**
 * Shared refresh / session-termination controller for the axios response interceptor.
 * Kept free of the axios package import so Jest can unit-test the loop-prevention logic.
 */
export function createAuthSessionController({ axiosInstance, localStorageService }) {
  let refreshInFlight = null;
  let sessionTerminationInFlight = null;

  async function terminateInvalidSession(navigate) {
    if (sessionTerminationInFlight) {
      return sessionTerminationInFlight;
    }

    sessionTerminationInFlight = (async () => {
      // Clear first so logout cannot re-enter refresh with a dead Bearer token.
      localStorageService.clearToken();
      try {
        await axiosInstance.get(apiPaths.LOGOUT, {
          skipAuthRefresh: true,
        });
      } catch (e) {
        // Logout may 401 when the session is already dead — terminal, not retryable.
        console.error(e);
      }
      navigate("/session/signin");
    })().finally(() => {
      sessionTerminationInFlight = null;
    });

    return sessionTerminationInFlight;
  }

  function refreshAccessToken() {
    if (!refreshInFlight) {
      refreshInFlight = axiosInstance
        .post(
          apiPaths.REFREST_TOKEN,
          { refreshToken: localStorageService.getRefreshToken() },
          { skipAuthRefresh: true }
        )
        .then((res) => {
          if (res.status !== 200) {
            throw new Error(`Refresh failed with status ${res.status}`);
          }
          const { jwt, refreshToken } = res.data;
          localStorageService.setToken({ access_token: jwt, refreshToken });
          return jwt;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  }

  function createResponseErrorHandler(navigate, openSnackbar) {
    return async (error) => {
      const originalRequest = error.config;

      if (!error.response) {
        return Promise.reject(error);
      }

      if (error.response.status === 402) {
        requestAnimationFrame(() => {
          openSnackbar(
            "Your subscription is in EXPIRED state. Please renew to continue.",
            "error"
          );
        });
        return Promise.reject(error);
      }

      if (shouldAttemptTokenRefresh(error, originalRequest)) {
        originalRequest._retry = true;

        try {
          const jwt = await refreshAccessToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `Bearer ${jwt}`;
          return axiosInstance(originalRequest);
        } catch (tokenError) {
          await terminateInvalidSession(navigate);
          return Promise.reject(tokenError);
        }
      }

      if (
        (error.response.status === 401 || error.response.status === 403) &&
        originalRequest?._retry &&
        !isAuthTerminalRequest(originalRequest.url || "") &&
        !originalRequest.skipAuthRefresh
      ) {
        await terminateInvalidSession(navigate);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    };
  }

  function resetForTests() {
    refreshInFlight = null;
    sessionTerminationInFlight = null;
  }

  return {
    createResponseErrorHandler,
    terminateInvalidSession,
    refreshAccessToken,
    resetForTests,
  };
}
